import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, IconButton, Paper, Slide, Typography, Button, Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import FastForwardIcon from '@mui/icons-material/FastForward';
import DialpadIcon from '@mui/icons-material/Dialpad';
import { useOrientation } from '../../hooks/useOrientation';
import { SHC_BOTTOM_NAV_CLEARANCE } from '../../constants/bottomNav';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import {
  AndroidTvDevice, androidtvControl, androidtvRefreshDevice,
} from '../../services/androidtvApi';
import {
  mapaAndroidTv,
  patchAndroidTvDevice,
  setAndroidTvPowerOptimistic,
} from '../../services/androidtvState';
import { requirePinIfNeeded } from '../../services/pinGate';
import { eventBus } from '../../services/EventBus';
import PowerButton from '../shared/PowerButton';
import AndroidTvActivityChart from './AndroidTvActivityChart';

const ACCENT = '#2391FF';
const ONLINE_GREEN = '#66bb6a';
/** Un poco más claro que Samsung base para contrastar con el fondo negro. */
const SURFACE = 'rgba(255,255,255,0.09)';
const SURFACE_BORDER = 'rgba(255,255,255,0.14)';
const PRESS = 'transform 140ms ease, background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease';

interface Props {
  open: boolean;
  device: AndroidTvDevice | null;
  onClose: () => void;
  onUpdated?: (d: AndroidTvDevice) => void;
}

function StatusChip({ color, label }: { color: string; label: string }) {
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.55,
      px: 0.9, py: 0.3, borderRadius: 999,
      bgcolor: `${color}18`, border: `1px solid ${color}55`, maxWidth: 118, flexShrink: 0,
    }}>
      <Box sx={{
        width: 6, height: 6, borderRadius: '50%', bgcolor: color,
        boxShadow: `0 0 8px ${color}`, flexShrink: 0,
        animation: 'atvPulse 2.2s ease-in-out infinite',
        '@keyframes atvPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.45 } },
      }} />
      <Typography sx={{ fontSize: 10, fontWeight: 600, color, letterSpacing: 0.15, lineHeight: 1.1 }}>
        {label}
      </Typography>
    </Box>
  );
}

/** Feedback de toque solo mientras está :active; evita hover/focus sticky en móvil. */
const keyBtnSx = {
  minWidth: 44, height: 40, borderRadius: '12px',
  bgcolor: SURFACE, border: `1px solid ${SURFACE_BORDER}`,
  color: '#fff', textTransform: 'none' as const, fontWeight: 700, fontSize: 12,
  transition: PRESS,
  WebkitTapHighlightColor: 'transparent',
  '&:hover': { bgcolor: SURFACE, borderColor: SURFACE_BORDER },
  '&:focus': { bgcolor: SURFACE, borderColor: SURFACE_BORDER },
  '&.Mui-focusVisible': { bgcolor: SURFACE, borderColor: SURFACE_BORDER },
  '&:active': { transform: 'scale(0.92)', bgcolor: 'rgba(35,145,255,0.18)' },
  '&.Mui-disabled': { color: '#555', borderColor: '#333' },
};

const blurAfterPress = (e: React.SyntheticEvent<HTMLElement>) => {
  // Quita el estado “oscuro” residual de focus/hover tras el toque
  const el = e.currentTarget;
  window.setTimeout(() => {
    try { el.blur(); } catch { /* noop */ }
  }, 120);
};

const AndroidTvDeviceModal: React.FC<Props> = ({ open, device, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const isHorizontal = useOrientation();
  const [isClosing, setIsClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [powerBusy, setPowerBusy] = useState(false);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [, setTick] = useState(0);
  const bodyScrollRef = useRef<HTMLDivElement | null>(null);
  const live = device ? (mapaAndroidTv[device.id] || device) : null;

  useEffect(() => {
    if (!keypadOpen && bodyScrollRef.current) {
      bodyScrollRef.current.scrollTop = 0;
    }
  }, [keypadOpen]);

  useEffect(() => {
    if (!open) return undefined;
    const unsub = eventBus.on('androidtv:updated', () => setTick((t) => t + 1));
    return () => unsub();
  }, [open]);

  const beginClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 280);
  }, [onClose]);

  const requestClose = useOverlayBackButton(open, beginClose, 'androidtv-device-modal');

  useEffect(() => {
    if (!open || !live?.id || !live.paired) return;
    void androidtvRefreshDevice(live.id).then((d) => {
      patchAndroidTvDevice(d);
      onUpdated?.(d);
    }).catch(() => undefined);
    const t = setInterval(() => {
      void androidtvRefreshDevice(live.id).then((d) => {
        patchAndroidTvDevice(d);
        onUpdated?.(d);
      }).catch(() => undefined);
    }, 8000);
    return () => clearInterval(t);
  }, [open, live?.id, live?.paired, onUpdated]);

  if (!live || !open) return null;

  const can = Boolean(live.can_control) && live.paired && live.online;
  const offline = !live.online;
  const statusColor = offline ? '#e57373' : (live.paired ? '#66bb6a' : '#e5a54b');
  const statusLabel = offline
    ? t('devices.offline')
    : (live.paired ? t('devices.online') : t('androidtv.modal.unpaired'));

  const run = async (action: string, value?: unknown) => {
    const exec = async () => {
      setError(null);
      try {
        if (action === 'power') {
          setAndroidTvPowerOptimistic(live.id, !Boolean(live.is_on));
        }
        if (action === 'volume' && (value === 'mute' || value === 'toggle_mute')) {
          patchAndroidTvDevice({ ...live, mute: !Boolean(live.mute) });
        }
        const res = await androidtvControl(live.id, action, value);
        if (res?.device) {
          if (action === 'power' && res.device.is_on != null) {
            setAndroidTvPowerOptimistic(live.id, Boolean(res.device.is_on));
          }
          // Si el STB no reporta mute, conservar el toggle local.
          if (action === 'volume' && (value === 'mute' || value === 'toggle_mute')) {
            const muted = res.device.mute != null ? Boolean(res.device.mute) : !Boolean(live.mute);
            patchAndroidTvDevice({ ...res.device, mute: muted });
            onUpdated?.(mapaAndroidTv[live.id] || { ...res.device, mute: muted });
          } else {
            patchAndroidTvDevice(res.device);
            onUpdated?.(mapaAndroidTv[live.id] || res.device);
          }
        }
      } catch (e: any) {
        setError(e?.response?.data?.detail || t('androidtv.modal.controlError'));
      }
    };
    if (!requirePinIfNeeded(live.pin_required, live.id, () => { void exec(); })) return;
    await exec();
  };

  const key = (code: string | number) => { void run('key', code); };
  const macro = (id: string) => { void run('macro', id); };

  const header = (
    <Box sx={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      px: 1, py: 0.65, borderBottom: `1px solid ${SURFACE_BORDER}`,
      background: 'linear-gradient(180deg, #1c1c1c 0%, #141414 100%)',
      minHeight: 40, flexShrink: 0, gap: 0.5,
    }}>
      <StatusChip color={statusColor} label={statusLabel} />
      <Box sx={{ flex: 1, textAlign: 'center', px: 0.5, minWidth: 0 }}>
        <Typography sx={{
          color: 'white', fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.15,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {live.nombre}
        </Typography>
        <Typography sx={{
          fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em',
          textTransform: 'uppercase', mt: 0.15,
        }}>
          Android TV
        </Typography>
      </Box>
      <IconButton onClick={requestClose} sx={{ color: 'white', p: 0.55, flexShrink: 0, transition: PRESS, '&:active': { transform: 'scale(0.9)' } }}>
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );

  const remote = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
      {/* Fila superior: Search / GUIDE / Info centrados e iguales; Power a la derecha */}
      <Box sx={{ position: 'relative', minHeight: 56, displayItems: 'center' }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 0.75,
          width: 'min(100%, 300px)',
          mx: 'auto',
        }}>
          <Button disableRipple disabled={!can} onClick={(e) => { key('SEARCH'); blurAfterPress(e); }} sx={{ ...keyBtnSx, width: '100%', px: 0.5 }} startIcon={<SearchIcon sx={{ fontSize: 16 }} />}>
            {t('androidtv.remote.search')}
          </Button>
          <Button disableRipple disabled={!can} onClick={(e) => { key('GUIDE'); blurAfterPress(e); }} sx={{ ...keyBtnSx, width: '100%', px: 0.5 }}>{t('androidtv.remote.guide')}</Button>
          <Button disableRipple disabled={!can} onClick={(e) => { key(165); blurAfterPress(e); }} sx={{ ...keyBtnSx, width: '100%', px: 0.5 }} startIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}>
            {t('androidtv.remote.info')}
          </Button>
        </Box>
        <Box sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          transform: 'translate(-15px, 60px)',
          zIndex: 1,
        }}>
          <PowerButton
            isOn={Boolean(live.is_on)}
            isUnknown={offline || !can}
            isLoading={powerBusy}
            deviceId={live.id}
            onClick={() => {
              setPowerBusy(true);
              void run('power', 'toggle').finally(() => setPowerBusy(false));
            }}
          />
        </Box>
      </Box>

      {/* D-pad + Rewind/Forward — 10px menos de aire respecto al renglón Guide */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25,
        py: 0.5,
        mt: '-10px',
        borderRadius: '20px',
        background: `
          radial-gradient(70% 80% at 50% 45%, ${ACCENT}18 0%, transparent 58%),
          radial-gradient(40% 50% at 50% 50%, ${ONLINE_GREEN}10 0%, transparent 55%)
        `,
      }}>
        <IconButton disableRipple disabled={!can} onClick={(e) => { key(89); blurAfterPress(e); }} sx={{ ...keyBtnSx, width: 48 }}>
          <FastRewindIcon />
        </IconButton>
        <Box sx={{
          width: 158, height: 158, borderRadius: '50%',
          border: `1px solid ${ACCENT}44`,
          bgcolor: SURFACE,
          boxShadow: `0 0 28px ${ACCENT}22, inset 0 0 24px rgba(35,145,255,0.08)`,
          backgroundImage: `radial-gradient(circle at 50% 45%, ${ACCENT}14 0%, transparent 55%)`,
          display: 'grid',
          gridTemplateAreas: `
            ". up ."
            "left ok right"
            ". down ."
          `,
          gridTemplateColumns: '1fr 1fr 1fr',
          gridTemplateRows: '1fr 1fr 1fr',
          placeItems: 'center',
        }}>
          <IconButton disableRipple disabled={!can} onClick={(e) => { key(19); blurAfterPress(e); }} sx={{ gridArea: 'up', color: '#fff', transition: PRESS, '&:active': { transform: 'scale(0.9)' } }}><KeyboardArrowUpIcon /></IconButton>
          <IconButton disableRipple disabled={!can} onClick={(e) => { key(21); blurAfterPress(e); }} sx={{ gridArea: 'left', color: '#fff', transition: PRESS, '&:active': { transform: 'scale(0.9)' } }}><KeyboardArrowLeftIcon /></IconButton>
          <Button
            disableRipple
            disabled={!can}
            onClick={(e) => { key(66); blurAfterPress(e); }}
            sx={{
              gridArea: 'ok', minWidth: 52, height: 52, borderRadius: '50%',
              bgcolor: `${ACCENT}33`, border: `1px solid ${ACCENT}88`, color: '#fff',
              fontWeight: 800, fontSize: 12, transition: PRESS,
              boxShadow: `0 0 18px ${ACCENT}40`,
              WebkitTapHighlightColor: 'transparent',
              '&:hover': { bgcolor: `${ACCENT}33` },
              '&:focus': { bgcolor: `${ACCENT}33` },
              '&.Mui-focusVisible': { bgcolor: `${ACCENT}33` },
              '&:active': { transform: 'scale(0.92)', bgcolor: 'rgba(35,145,255,0.45)' },
            }}
          >
            {t('androidtv.remote.ok')}
          </Button>
          <IconButton disableRipple disabled={!can} onClick={(e) => { key(22); blurAfterPress(e); }} sx={{ gridArea: 'right', color: '#fff', transition: PRESS, '&:active': { transform: 'scale(0.9)' } }}><KeyboardArrowRightIcon /></IconButton>
          <IconButton disableRipple disabled={!can} onClick={(e) => { key(20); blurAfterPress(e); }} sx={{ gridArea: 'down', color: '#fff', transition: PRESS, '&:active': { transform: 'scale(0.9)' } }}><KeyboardArrowDownIcon /></IconButton>
        </Box>
        <IconButton disableRipple disabled={!can} onClick={(e) => { key(90); blurAfterPress(e); }} sx={{ ...keyBtnSx, width: 48 }}>
          <FastForwardIcon />
        </IconButton>
      </Box>

      {/* Mute / Exit / Back — misma grilla 3 col que VOL/CH para alinear anchos */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
        <Button
          disableRipple
          disabled={!can}
          onClick={(e) => { void run('volume', 'mute'); blurAfterPress(e); }}
          sx={{
            ...keyBtnSx,
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            width: '100%',
            px: 0.5,
            color: live.mute ? '#ef5350' : '#fff',
            borderColor: live.mute ? 'rgba(239,83,80,0.45)' : SURFACE_BORDER,
            bgcolor: live.mute ? 'rgba(239,83,80,0.12)' : SURFACE,
            '&:hover': {
              bgcolor: live.mute ? 'rgba(239,83,80,0.12)' : SURFACE,
              borderColor: live.mute ? 'rgba(239,83,80,0.45)' : SURFACE_BORDER,
            },
            '&:focus': {
              bgcolor: live.mute ? 'rgba(239,83,80,0.12)' : SURFACE,
              borderColor: live.mute ? 'rgba(239,83,80,0.45)' : SURFACE_BORDER,
            },
            '&.Mui-focusVisible': {
              bgcolor: live.mute ? 'rgba(239,83,80,0.12)' : SURFACE,
              borderColor: live.mute ? 'rgba(239,83,80,0.45)' : SURFACE_BORDER,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: 0 }}>
            {live.mute
              ? <VolumeOffIcon sx={{ fontSize: 16, color: '#ef5350' }} />
              : <VolumeUpIcon sx={{ fontSize: 16 }} />}
          </Box>
          <Box component="span" sx={{ px: 0.15, lineHeight: 1 }}>
            {t('androidtv.remote.mute')}
          </Box>
          <Box
            component="span"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: 13,
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
              minWidth: 0,
              color: live.mute ? 'rgba(239,83,80,0.9)' : 'rgba(255,255,255,0.92)',
            }}
          >
            {live.volume != null ? live.volume : '—'}
          </Box>
        </Button>
        <Button disableRipple disabled={!can} onClick={(e) => { key(3); blurAfterPress(e); }} sx={{ ...keyBtnSx, width: '100%' }} startIcon={<HomeOutlinedIcon sx={{ fontSize: 16 }} />}>
          {t('androidtv.remote.exit')}
        </Button>
        <Button disableRipple disabled={!can} onClick={(e) => { key(4); blurAfterPress(e); }} sx={{ ...keyBtnSx, width: '100%' }} startIcon={<UndoIcon sx={{ fontSize: 16 }} />}>
          {t('androidtv.remote.back')}
        </Button>
      </Box>

      {/* Misma grilla 3 col: VOL bajo Mute, Flow bajo Exit, CH bajo Back */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateRows: 'auto auto',
        gridTemplateAreas: `
          "volup flow chup"
          "voldown flow chdown"
        `,
        gap: 0.75,
        alignItems: 'center',
      }}>
        <Button disableRipple disabled={!can} onClick={(e) => { void run('volume', 'up'); blurAfterPress(e); }} sx={{ ...keyBtnSx, gridArea: 'volup', width: '100%' }}>{t('androidtv.remote.volUp')}</Button>
        <Button disableRipple disabled={!can} onClick={(e) => { key(166); blurAfterPress(e); }} sx={{ ...keyBtnSx, gridArea: 'chup', width: '100%' }}>{t('androidtv.remote.chUp')}</Button>
        <Box sx={{
          gridArea: 'flow',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Button
            disableRipple
            disabled={!can}
            onClick={(e) => { macro('open_iptv_app'); blurAfterPress(e); }}
            sx={{
              width: 72, height: 72, minWidth: 72, borderRadius: '50%',
              bgcolor: `${ONLINE_GREEN}28`,
              border: `1px solid ${ONLINE_GREEN}99`,
              color: '#fff',
              fontWeight: 800,
              fontSize: 13,
              letterSpacing: 0.4,
              transition: PRESS,
              boxShadow: `0 0 22px ${ONLINE_GREEN}40, inset 0 0 12px ${ONLINE_GREEN}18`,
              WebkitTapHighlightColor: 'transparent',
              '&:hover': { bgcolor: `${ONLINE_GREEN}28` },
              '&:focus': { bgcolor: `${ONLINE_GREEN}28` },
              '&.Mui-focusVisible': { bgcolor: `${ONLINE_GREEN}28` },
              '&:active': { transform: 'scale(0.92)', bgcolor: 'rgba(102,187,106,0.42)' },
              '&.Mui-disabled': { color: '#555', borderColor: '#333', boxShadow: 'none' },
            }}
          >
            Flow
          </Button>
        </Box>
        <Button disableRipple disabled={!can} onClick={(e) => { void run('volume', 'down'); blurAfterPress(e); }} sx={{ ...keyBtnSx, gridArea: 'voldown', width: '100%' }}>{t('androidtv.remote.volDown')}</Button>
        <Button disableRipple disabled={!can} onClick={(e) => { key(167); blurAfterPress(e); }} sx={{ ...keyBtnSx, gridArea: 'chdown', width: '100%' }}>{t('androidtv.remote.chDown')}</Button>
      </Box>

      {/* Streaming */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.6 }}>
        <Button disableRipple disabled={!can} onClick={(e) => { macro('netflix'); blurAfterPress(e); }} sx={{ ...keyBtnSx, fontSize: 10, minWidth: 0, px: 0.5 }}>Netflix</Button>
        <Button disableRipple disabled={!can} onClick={(e) => { macro('youtube'); blurAfterPress(e); }} sx={{ ...keyBtnSx, fontSize: 10, minWidth: 0, px: 0.5 }}>YouTube</Button>
        <Button disableRipple disabled={!can} onClick={(e) => { macro('prime'); blurAfterPress(e); }} sx={{ ...keyBtnSx, fontSize: 10, minWidth: 0, px: 0.5 }}>Prime</Button>
        <Button disableRipple disabled={!can} onClick={(e) => { macro('disney'); blurAfterPress(e); }} sx={{ ...keyBtnSx, fontSize: 10, minWidth: 0, px: 0.5 }}>Disney+</Button>
      </Box>

      {/* Keypad expandible */}
      <Button
        disableRipple
        onClick={(e) => { setKeypadOpen((v) => !v); blurAfterPress(e); }}
        startIcon={<DialpadIcon />}
        sx={{ ...keyBtnSx, width: '100%' }}
      >
        {keypadOpen ? t('androidtv.modal.hideKeypad') : t('androidtv.modal.showKeypad')}
      </Button>
      <Collapse in={keypadOpen}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.6 }}>
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <Button key={d} disableRipple disabled={!can} onClick={(e) => { key(d); blurAfterPress(e); }} sx={{ ...keyBtnSx, fontSize: 18, fontWeight: 800 }}>{d}</Button>
          ))}
          <Box />
          <Button disableRipple disabled={!can} onClick={(e) => { key('0'); blurAfterPress(e); }} sx={{ ...keyBtnSx, fontSize: 18, fontWeight: 800 }}>0</Button>
          <Box />
        </Box>
      </Collapse>

      <Box sx={{ mt: 0.5 }}>
        <AndroidTvActivityChart deviceId={live.id} chartHeight={105} />
      </Box>
    </Box>
  );

  const body = (
    <Box
      ref={bodyScrollRef}
      sx={{
      flex: 1, minHeight: 0,
      // Scroll solo con teclado numérico expandido
      overflowY: 'auto',
      overflowX: 'hidden',
      px: 1.75, py: 1.1,
      display: 'flex', flexDirection: 'column', gap: 1.1,
      background: `
        radial-gradient(120% 60% at 50% -10%, rgba(35,145,255,0.12) 0%, transparent 50%),
        radial-gradient(80% 40% at 85% 20%, ${ONLINE_GREEN}10 0%, transparent 45%)
      `,
    }}>
      {!live.paired && (
        <Typography sx={{ fontSize: 12, color: '#e5a54b', textAlign: 'center' }}>
          {t('androidtv.modal.pairingRequired')}
        </Typography>
      )}
      {remote}
      {error && (
        <Box sx={{
          px: 1.25, py: 0.85, borderRadius: '12px',
          bgcolor: 'rgba(229,115,115,0.12)', border: '1px solid rgba(229,115,115,0.35)',
        }}>
          <Typography sx={{ color: '#ef9a9a', fontSize: 12, textAlign: 'center' }}>{String(error)}</Typography>
        </Box>
      )}
    </Box>
  );

  if (isHorizontal) {
    return (
      <Box sx={{ width: '100%', height: '100%', backgroundColor: '#000', color: 'white', display: 'flex', flexDirection: 'column' }}>
        <Slide direction="left" in={open && !isClosing} timeout={300} appear mountOnEnter unmountOnExit>
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Paper sx={{
              width: '100%', height: '100%', backgroundColor: '#000', color: 'white',
              display: 'flex', flexDirection: 'column', borderRadius: 0, boxShadow: 'none',
            }}>
              {header}
              {body}
            </Paper>
          </Box>
        </Slide>
      </Box>
    );
  }

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 1360,
      display: open ? 'flex' : 'none',
      alignItems: 'center', justifyContent: 'center',
    }}>
      <Box onClick={requestClose} sx={{ position: 'absolute', inset: 0, backgroundColor: 'transparent' }} />
      <Slide direction="left" in={open && !isClosing} timeout={300} appear mountOnEnter unmountOnExit>
        <Paper sx={{
          position: 'fixed', bottom: SHC_BOTTOM_NAV_CLEARANCE, top: '40px', left: '1%', right: '1%', width: '98%',
          backgroundColor: '#000', color: 'white', zIndex: 1400,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          borderRadius: '16px 16px 0 0',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.5)',
          border: '1px solid #444', borderTop: '2px solid #2391FF',
        }}>
          {header}
          {body}
        </Paper>
      </Slide>
    </Box>
  );
};

export default AndroidTvDeviceModal;
